import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useConvex } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Doc } from 'convex/_generated/dataModel'

export const Route = createFileRoute('/storage-assets')({
  component: RouteComponent,
})

function RouteComponent() {
  return <StorageAssets />
}

function StorageAssets() {
  const convex = useConvex()

  // Query all generations
  const { data: allGenerations, isLoading, error } = useQuery({
    queryKey: ['allGenerations'],
    queryFn: async () => {
      return await convex.query(api.tempolor.listByUser, { limit: 100 })
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  })

  // Filter for generations with storage IDs
  const storedGenerations = allGenerations?.filter((gen: { storageId?: string }) => gen.storageId) || []

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Storage Assets Library
          </h1>
          <p className="text-gray-600">
            All audio files saved to Convex storage from Tempolor API
          </p>
        </div>

        {isLoading && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-600">Loading storage assets...</p>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-red-600">Error loading assets: {error instanceof Error ? error.message : 'Unknown error'}</p>
          </div>
        )}

        {!isLoading && !error && storedGenerations.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-500">
              No stored assets yet. Generate some audio on the{' '}
              <a href="/example-song" className="text-blue-600 hover:underline">
                Example Song
              </a>{' '}
              page and save them to storage!
            </p>
          </div>
        )}

        {!isLoading && !error && storedGenerations.length > 0 && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Total Stored Assets:</strong> {storedGenerations.length}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {storedGenerations.map((gen: Doc<'temporGenerations'>) => (
                <StorageAssetCard key={gen._id} generation={gen} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function StorageAssetCard({ generation }: { generation: Doc<'temporGenerations'> }) {
  const convex = useConvex()

  // Query storage URL for this asset
  const { data: storageUrl } = useQuery({
    queryKey: ['storageUrl', generation.storageId],
    queryFn: async () => {
      if (!generation.storageId) return null
      return await convex.query(api.tempolor.getStorageUrl, {
        storageId: generation.storageId,
      })
    },
    enabled: !!generation.storageId,
  })

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-500 border-green-500'
      case 'processing':
        return 'bg-blue-500/20 text-blue-500 border-blue-500'
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-500 border-yellow-500'
      case 'failed':
        return 'bg-red-500/20 text-red-500 border-red-500'
      default:
        return 'bg-gray-500/20 text-gray-500 border-gray-500'
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {generation.type.charAt(0).toUpperCase() + generation.type.slice(1)} Generation
        </h3>
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(generation.status)}`}>
          {generation.status}
        </span>
      </div>

      <div className="space-y-3">
        {/* Metadata */}
        <div className="space-y-2">
          <div className="text-sm">
            <span className="font-medium text-gray-700">Created:</span>{' '}
            <span className="text-gray-600">{new Date(generation.createdAt).toLocaleString()}</span>
          </div>

          {generation.prompt && (
            <div className="text-sm">
              <span className="font-medium text-gray-700">Prompt:</span>{' '}
              <p className="text-gray-600 mt-1">{generation.prompt}</p>
            </div>
          )}

          {generation.model && (
            <div className="text-sm">
              <span className="font-medium text-gray-700">Model:</span>{' '}
              <span className="text-gray-600">{generation.model}</span>
            </div>
          )}

          {generation.storageId && (
            <div className="text-sm">
              <span className="font-medium text-gray-700">Storage ID:</span>{' '}
              <span className="text-gray-600 font-mono text-xs">{generation.storageId}</span>
            </div>
          )}
        </div>

        {/* Audio Player */}
        {storageUrl && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Audio Playback:
            </label>
            <audio controls className="w-full">
              <source src={storageUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
            <div className="mt-2 flex items-center text-sm text-green-600">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Stored in Convex
            </div>
          </div>
        )}

        {/* Generated Lyrics */}
        {generation.generatedLyrics && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lyrics:
            </label>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md max-h-40 overflow-auto">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                {generation.generatedLyrics}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
